import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  AjaxSettingsModel,
  BeforeDownloadEventArgs,
  BeforeSendEventArgs,
  DetailsViewService,
  DetailsViewSettingsModel,
  FileData,
  FileManagerModule,
  NavigationPaneService,
  ToolbarService,
  VirtualizationService
} from '@syncfusion/ej2-angular-filemanager';
import { ActivatedRoute, ParamMap } from '@angular/router';
import { of, Subscription, switchMap, timer } from 'rxjs';
import { Location } from '@angular/common';
import { BasicUser } from '../shared/basic-user.model';
import { EaccessAuthService } from '../auth/eaccess-auth.service';
import { UsersService } from './users.service';
import { CigpStaffService } from '../cigp-staff/cigp-staff.service';
import { environment } from '../../environments/environment';
import $ from 'jquery';

@Component({
  selector: 'eaa-user-documents',
  imports: [FileManagerModule],
  providers: [DetailsViewService, NavigationPaneService, ToolbarService, VirtualizationService],
  template: `
    <div class="card">
      <div class="card-header">
        <h4 class="card-title m-0">{{ user?.displayName }} | Documents</h4>
      </div>
      <div class="card-body p-0">
        @if (ajaxSettings) {
          <ejs-filemanager
            [ajaxSettings]="ajaxSettings"
            view="Details"
            [enableVirtualization]="true"
            [detailsViewSettings]="detailsViewSettings"
            [navigationPaneSettings]="{ visible: false }"
            height="700px"
            (beforeDownload)="onBeforeDownload($event)"
            (beforeSend)="onBeforeSend($event)"
            (fileLoad)="onBeforeFileLoad($event)"
          />
        }
      </div>
      <div class="card-footer">
        <button class="btn btn-outline-secondary" (click)="location.back()">BACK</button>
      </div>
    </div>
  `
})
export class UserDocumentsComponent implements OnInit, OnDestroy {
  public view?: any;
  public ajaxSettings: AjaxSettingsModel | null = null;
  public user: BasicUser | null = null;
  private accessToken: string | null = null;
  private acquireAcessTokenSubscription: Subscription | null = null;

  protected detailsViewSettings: DetailsViewSettingsModel = {
    columns: [
      {
        field: 'name',
        headerText: 'File Name',
        minWidth: 120,
        width: 'auto',
        customAttributes: { class: 'e-fe-grid-name' },
        template: '<span class="e-fe-text">${name}</span>'
      },
      {
        field: 'dateCreatedStr',
        headerText: 'File Date',
        minWidth: 50,
        width: '190',
        template: '${if(dateCreatedStr === undefined)}<span></span>${else}<span>${dateCreatedStr}</span>${/if}'
      },
      {
        field: 'sizeStr',
        headerText: 'File Size',
        minWidth: 50,
        width: '110',
        template: '${if(sizeStr === undefined)}<span></span>${else}<span>${sizeStr}</span>${/if}'
      }
    ]
  };

  constructor(
    private authService: EaccessAuthService,
    private route: ActivatedRoute,
    private usersService: UsersService,
    private cigpStaffService: CigpStaffService,
    public location: Location
  ) {}

  public ngOnInit(): void {
    this.acquireAcessTokenSubscription = timer(0, 120000)
      .pipe(switchMap(() => this.authService.getAccessToken$()))
      .subscribe((token) => (this.accessToken = token));

    this.route.paramMap
      .pipe(
        switchMap((params: ParamMap) => {
          const id = params.get('id');
          return id && id !== undefined
            ? params.get('type') === 'staff'
              ? this.cigpStaffService.getStaffFromCache$(id)
              : this.usersService.getUserFromCache$(id)
            : of(null);
        })
      )
      .subscribe((user) => {
        if (user) {
          this.user = user;
          this.ajaxSettings = {
            url: `${environment.apiEndpoint}/api/documents/${user.id}/action`,
            downloadUrl: `${environment.apiEndpoint}/api/documents/${user.id}/download`,
            uploadUrl: `${environment.apiEndpoint}/api/documents/${user.id}/upload`
          };
        }
      });
  }

  public ngOnDestroy(): void {
    this.acquireAcessTokenSubscription?.unsubscribe();
  }

  public onBeforeFileLoad(args: any) {
    if (!args.fileDetails.viewed) {
      $(args.element).addClass('fw-bold');
    }
  }

  public onBeforeSend(args: BeforeSendEventArgs): void {
    if (args.ajaxSettings) {
      const accessToken = this.accessToken;
      (args.ajaxSettings as any).beforeSend = function (args: any) {
        args.httpRequest.setRequestHeader('Authorization', `Bearer ${accessToken}`);
      };
    }
  }

  public onBeforeDownload(args: BeforeDownloadEventArgs) {
    args.useFormPost = false;
    if (args.ajaxSettings) {
      const accessToken = this.accessToken;
      (args.ajaxSettings as any).beforeSend = function (args: any) {
        args.fetchRequest.headers.append('Authorization', `Bearer ${accessToken}`);
      };
    }

    if (args.data) {
      // Backend expects the path to be found in field filterPath
      const data = args.data as { path: string; data: FileData[] };

      if (data.data && data.data.length > 0) {
        for (const file of data.data) {
          file.filterPath = data.path;
        }
      }
    }
  }
}
